import { GroupCellRendererParams, ITextFilterParams } from 'ag-grid-community';
import { Icon, Tag, Text } from 'components/ui';
import { format } from 'date-fns';
import Link from 'next/link';
import React from 'react';
import { DATE_FORMAT } from 'settings/config';
import styled from 'styled-components';
import { globalTheme } from 'styles/global-theme';
import { getLivestockTypeIconName, getLivestockTypeName } from './helpers';

export interface IPillStylingProps {
  backgroundColor?: keyof typeof globalTheme.colors;
  color?: keyof typeof globalTheme.colors;
}

export interface IPillConditionalProps {
  value: string | number | boolean | undefined;
  styling?: IPillStylingProps;
}

const CenteredText = styled(Text)`
  text-align: center;
`;

const StyledTag = styled(Tag)`
  height: 32px;
  margin: 4px;
  padding: 0 12px;
  display: flex;
  width: max-content;
  align-items: center;
  justify-content: center;
  p {
    width: max-content;
  }
`;

export const FlexContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${globalTheme.space[0]};
  height: 100%;
  p {
    width: max-content;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const StyledLink = styled(Link)`
  color: ${globalTheme.colors.blue};
`;

const LivestockTag = styled(Tag)`
  display: flex;
  align-items: center;
  margin-top: 5px;
  width: max-content;
  background-color: ${globalTheme.colors.blue};
  color: ${globalTheme.colors.white};
  padding: 15px 8px 15px 4px;
  * {
    fill: ${globalTheme.colors.black};
  }
  svg {
    padding: 3px;
    background-color: ${globalTheme.colors.white};
    border-radius: 50%;
  }
  p {
    width: max-content;
  }
`;

/**
 * Generates random styling based on IPillStylingProps interface
 * @returns IPillStylingProps
 */
export const generateRandomStyling = (): IPillStylingProps => {
  const colors = [
    'darkBlue',
    'amber',
    'pink',
    'purple',
    'green',
    'black',
    'blue',
    'highlightBar',
    'lightGreen',
    'lightRed',
    'neutral',
    'orange',
  ];

  return {
    color: 'white',
    backgroundColor: getRandomItem(colors) as keyof typeof globalTheme.colors,
  };
};

/**
 * Returns a random item from an array that contains 'dark' as prefix
 * @param arr Array of items
 * @returns Random item from the array
 */
const getRandomItem = (arr: string[]): string => {
  const randomIndex = Math.floor(Math.random() * arr.length);
  return arr[randomIndex];
};

/**
 *
 * @param value string | number | boolean
 * @param condition IPillConditionalProps | IPillConditionalProps[]
 * @returns IPillStylingProps
 */
const getStyling = (
  value: string | number | boolean,
  condition?: IPillConditionalProps | IPillConditionalProps[]
): IPillStylingProps => {
  if (Array.isArray(condition)) {
    const matchingCondition = condition.find((cond) => cond.value === value);
    return matchingCondition?.styling || {};
  } else if (condition?.value === value) {
    return condition.styling || {};
  }
  return {};
};

/**
 *
 * @param params Ag-Grid Cell Renderer Params
 * @returns single formatted date (default 'd LLL yyyy')
 */
export const dateCellRenderer = (
  params: GroupCellRendererParams,
  dateFormat?: string
): string => {
  return params.value
    ? format(new Date(params.value), dateFormat || DATE_FORMAT.listing)
    : '-';
};

/**
 *
 * @param params Ag-Grid Cell Renderer Params
 * @returns Yes or No in pills
 */
export const booleanCellRenderer = (
  params: GroupCellRendererParams
): React.ReactNode => {
  if (params.value !== null && typeof params.value === 'boolean') {
    return params.value ? (
      <Tag color='white' backgroundColor='green'>
        Yes
      </Tag>
    ) : (
      <Tag color='white' backgroundColor='red'>
        No
      </Tag>
    );
  } else {
    return <Text>-</Text>;
  }
};

/**
 *
 * @param params Ag-Grid Cell Renderer Params
 * @returns string values
 */
export const stringCellRenderer = (
  params: GroupCellRendererParams
): React.ReactNode => {
  if (
    params.value !== null &&
    params.value !== undefined &&
    params.value !== ''
  ) {
    return (
      <FlexContainer>
        <Text>{params.value}</Text>
      </FlexContainer>
    );
  } else {
    return (
      <FlexContainer>
        <Text>-</Text>
      </FlexContainer>
    );
  }
};

/**
 *
 * @param params Ag-Grid Cell Renderer Params
 * @returns pills for displaying string or array values
 */
export const pillCellRenderer = (
  params: GroupCellRendererParams,
  condition?: IPillConditionalProps | IPillConditionalProps[]
): React.ReactNode => {
  if (params.value !== null && params.value !== undefined) {
    if (typeof params.value === 'string') {
      const styling = getStyling(params.value, condition);
      return (
        <Tag color={styling.color} backgroundColor={styling.backgroundColor}>
          {params.value}
        </Tag>
      );
    } else if (Array.isArray(params.value)) {
      return (
        <FlexContainer>
          {params.value.map((item, index) => {
            const styling = getStyling(item, condition);
            return (
              <Tag
                key={index}
                color={styling.color}
                backgroundColor={styling.backgroundColor}
              >
                {item}
              </Tag>
            );
          })}
        </FlexContainer>
      );
    } else {
      return <CenteredText>-</CenteredText>;
    }
  } else {
    return <CenteredText>-</CenteredText>;
  }
};

/**
 *
 * @param params Ag-Grid Cell Renderer Params
 * @returns pills for displaying buy, sell values
 */
export const buySellCellRenderer = (
  params: GroupCellRendererParams
): React.ReactNode => {
  if (params.value === 'BUY') {
    return (
      <StyledTag backgroundColor='lightBlue'>
        <Icon size={1} color='blue' component='IncomingIcon' />
        <Text color='blue' fontSize={1} fontWeight='medium'>
          BUY
        </Text>
      </StyledTag>
    );
  } else if (params.value === 'SELL') {
    return (
      <StyledTag backgroundColor='lightPink'>
        <Icon size={1} color='pink' component='OutgoingIcon' />
        <Text color='pink' fontSize={2} fontWeight='medium'>
          Sell
        </Text>
      </StyledTag>
    );
  } else {
    return <CenteredText>-</CenteredText>;
  }
};

/**
 *
 * @param params Ag-Grid Cell Renderer Params
 * @returns formatted('en-AU') number
 */
export const numberCellRenderer = (
  params: GroupCellRendererParams,
  suffix?: string
): React.ReactNode => (
  <FlexContainer>
    {params.value !== null &&
    params.value !== undefined &&
    params.value !== '' ? (
      <>
        <Text>{params.value.toLocaleString('en-AU')}</Text>
        {suffix && <Text>{suffix}</Text>}
      </>
    ) : (
      <Text>-</Text>
    )}
  </FlexContainer>
);

/**
 *
 * @param params Ag-Grid Cell Renderer Params
 * @returns product type cells
 */
export const productTypeCellRenderer = (
  params: GroupCellRendererParams
): React.ReactNode => {
  return params.value ? (
    <LivestockTag>
      <Icon component={getLivestockTypeIconName(params.value)} size={2} />
      <Text fontSize={1}>{getLivestockTypeName(params.value)}</Text>
    </LivestockTag>
  ) : (
    <Text>-</Text>
  );
};

/**
 *
 * @param params Ag-Grid Cell Renderer Params
 * @returns comma separated string for array values
 */
export const arrayCellRenderer = (
  params: GroupCellRendererParams
): React.ReactNode => {
  return (
    <FlexContainer>
      {Array.isArray(params.value) ? (
        params.value.map((item, index) => {
          return <Text key={index}>{item}</Text>;
        })
      ) : (
        <Text>-</Text>
      )}
    </FlexContainer>
  );
};

/**
 *
 * @param params Ag-Grid Cell Renderer Params
 * @returns comma separated string for array values
 */
export const linkCellRenderer = (
  params: GroupCellRendererParams,
  link: string
): React.ReactNode => {
  return (
    <FlexContainer>
      <StyledLink href={link}>
        <Text>{params.value}</Text>
      </StyledLink>
    </FlexContainer>
  );
};

// Filtering

function contains(target: string, lookingFor: string) {
  return target && target.indexOf(lookingFor) >= 0;
}

export var productFilterParams: ITextFilterParams = {
  filterOptions: ['contains'],
  textMatcher: ({ value, filterText }) => {
    var aliases: Record<string, string> = {
      CT: 'Cattle',
      GT: 'Goat',
      SH: 'Sheep',
    };
    var literalMatch = contains(value, filterText || '');
    return !!literalMatch || !!contains(value, aliases[filterText || '']);
  },
  trimInput: true,
  debounceMs: 1000,
  maxNumConditions: 1,
};

/**
 *
 * @param item array of string that might be null or undefined
 * @returns array or undefined
 */
export const getArrayItem = (item: string[] | undefined | null) => {
  if (item && item.length) {
    return item;
  } else {
    return undefined;
  }
};
